import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * useSubscriptionAccess — fetches the current user's capability map from
 * GET /api/subscription/access. Single source of truth for feature gating.
 *
 * Returns: { access, loading, tier, has, refresh }
 *   access  — full object from server
 *   tier    — shorthand for access.tier (e.g. 'beginner', 'advance', 'pro')
 *   has(k)  — helper: has('adaptive') === access.has_adaptive
 *   refresh — re-fetch (call after a successful checkout)
 */
export default function useSubscriptionAccess() {
  const [access, setAccess] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchAccess = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setAccess(null);
      setLoading(false);
      return;
    }
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/subscription/access`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAccess(data);
    } catch (err) {
      console.error('Failed to fetch subscription access:', err);
      setAccess(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAccess(); }, [fetchAccess]);

  const has = useCallback(
    (capability) => Boolean(access && access[`has_${capability}`]),
    [access]
  );

  return {
    access,
    tier: access?.tier || 'none',
    tierLabel: access?.tier_label || '',
    loading,
    has,
    refresh: fetchAccess,
  };
}
